import { Transforms, Editor, Element as SlateElement, Range } from 'slate';

import isUrl from 'is-url';

import { useSlate } from 'slate-react';
import React from 'react';
import { Icon } from './Icon';
import { EditorButton } from './EditorButton';
import { LinkElement } from 'types/comment';
import { isValidUrl } from 'lib/isValidUrl';

const insertLink = (editor: Editor, url: string) => {
  if (editor.selection) {
    wrapLink(editor, url);
  }
};

const isLinkActive = (editor: Editor) => {
  const [link] = Array.from(
    Editor.nodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
    })
  );
  return !!link;
};
const unwrapLink = (editor: Editor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
};

const wrapLink = (editor: Editor, url: string) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link: LinkElement = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

export const LinkButton = () => {
  function promptForUrl(prompt: string, previous?: string): string {
    const url = window.prompt(prompt, previous);
    if (!url) return '';
    if (!isValidUrl(url)) {
      return promptForUrl(
        'URL rejected, please include the provide url, e.g https://rive.app',
        url
      );
    }
    return url;
  }
  const editor = useSlate();
  return (
    <EditorButton
      active={isLinkActive(editor)}
      onMouseDown={(event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        const url = promptForUrl('Enter the URL of the link');
        if (!url) return;
        insertLink(editor, url);
      }}
    >
      <Icon>link</Icon>
    </EditorButton>
  );
};

export const RemoveLinkButton = () => {
  const editor = useSlate();

  return (
    <EditorButton
      active={isLinkActive(editor)}
      onMouseDown={() => {
        if (isLinkActive(editor)) {
          unwrapLink(editor);
        }
      }}
    >
      <Icon>link_off</Icon>
    </EditorButton>
  );
};

export const withLinks = (editor: Editor) => {
  const { insertData, insertText, isInline } = editor;

  editor.isInline = (element) => {
    return element.type === 'link' ? true : isInline(element);
  };

  editor.insertText = (text) => {
    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertText(text);
    }
  };

  editor.insertData = (data) => {
    const text = data.getData('text/plain');

    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertData(data);
    }
  };

  return editor;
};
